import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    Output
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    Validator
} from '@angular/forms';
import {
    MissionDetailsDto,
    MissionRoleDto,
    observeFormControl,
    PersonRolesDto,
    WorkGroup
} from '@flyfreely-portal-ui/flyfreely';
import { FormatPersonPipe, personSearch } from '@flyfreely-portal-ui/ui';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
    MissionEditService,
    PersonWithRegisterStatus
} from '../mission-edit.service';
import { MissionRpicSelectDataService } from './mission-rpic-select-data.service';

export const MISSION_RPIC_SELECT_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MissionRpicSelect),
    multi: true
};

export const MISSION_RPIC_SELECT_VALIDATOR: any = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => MissionRpicSelect),
    multi: true
};

@Component({
    selector: '[mission-rpic-select]',
    templateUrl: './mission-rpic-select.component.html',
    providers: [
        MISSION_RPIC_SELECT_ACCESSOR,
        MISSION_RPIC_SELECT_VALIDATOR,
        MissionRpicSelectDataService
    ]
})
export class MissionRpicSelect implements ControlValueAccessor, Validator {
    @Input() roles: MissionRoleDto[];
    @Input() disable: boolean;
    @Input() isLoading: boolean;
    @Input() readonly: boolean;
    @Input() pilotRegisterDisabled = true;
    @Input() orgId: number;
    @Input() showStatus = true;

    @Output() deleteCrew = new EventEmitter<void>();
    @Output() toggleShowDetails = new EventEmitter<void>();
    @Output() showPersonDetails = new EventEmitter<number>();

    missionDetails: MissionDetailsDto;
    pilots: PersonWithRegisterStatus[];
    crewLookup: { [id: number]: string } = {};
    private ngUnsubscribe$ = new Subject<void>();
    selectPersonnelRegisterStatus = new BehaviorSubject<string>('');
    showRpicRegisterDetails = false;
    authorityIds: number[];

    selectedWorkgroup: WorkGroup;

    personSearch = personSearch.bind(this);

    registerEntriesOnMissionLoading = false;
    selectPerson = true;

    hasRpa = false;

    rpicId: number;
    rpicRoleId: number;
    onChange = Function.prototype;
    onTouched = Function.prototype;

    loadingPersonnel = false;

    /**
     * The organisation roles that qualify a person to act as RPIC.
     * Used both when fetching the eligible personnel list and when
     * determining whether the currently selected person is still valid.
     * Update this list if the qualifying roles ever change.
     */
    private static readonly REQUIRED_RPIC_ROLES: PersonRolesDto.Roles[] = [
        PersonRolesDto.Roles.PILOT,
        PersonRolesDto.Roles.STUDENT
    ];

    private get rpicGuardReady(): boolean {
        return (
            this.rpicId != null &&
            !this.loadingPersonnel &&
            this.pilots != null &&
            this.pilots.length > 0
        );
    }

    get rpicNotInOrg(): boolean {
        if (!this.rpicGuardReady) {
            return false;
        }
        return this.pilots.find(p => p.id === this.rpicId) == null;
    }

    get rpicMissingRole(): boolean {
        if (!this.rpicGuardReady) {
            return false;
        }
        const person = this.pilots.find(p => p.id === this.rpicId);
        if (person == null) {
            return false;
        }
        return !MissionRpicSelect.REQUIRED_RPIC_ROLES.some(role =>
            person.roles?.includes(role)
        );
    }

    get rpicNotAvailable(): boolean {
        return this.rpicNotInOrg || this.rpicMissingRole;
    }

    constructor(
        private missionEditService: MissionEditService,
        private missionRpicSelectDataService: MissionRpicSelectDataService,
        private changeDetector: ChangeDetectorRef,
        private formatPersonPipe: FormatPersonPipe
    ) {}

    /* ngOnChanges(changes: SimpleChanges) {
        if (changes['roles'] && this.roles?.length === 1) {
            // When there is exactly one RPIC role, auto-apply it so the control
            // value always carries a valid rpicRoleId even without a dropdown.
            if (this.rpicRoleId !== this.roles[0].id) {
                this.rpicRoleId = this.roles[0].id;
                if (this.rpicId != null) {
                    this.onUpdate();
                }
            }
        }
    } */

    ngOnInit() {
        this.missionRpicSelectDataService.working$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => {
                this.loadingPersonnel = working;
                this.changeDetector.markForCheck();
            });

        this.missionEditService.registerEntriesOnMissionLoading$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                loading => (this.registerEntriesOnMissionLoading = loading)
            );

        observeFormControl<number[]>(
            this.missionEditService.missionForm.controls.craftIds
        )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(rpaIds => {
                this.hasRpa = rpaIds != null && rpaIds.length > 0;
            });

        combineLatest([
            observeFormControl<WorkGroup>(
                this.missionEditService.missionObjectivesGroup.controls
                    .workgroup
            ),
            this.missionEditService.savedMission$,
            observeFormControl<{ rpicId: number; rpicRoleId: number }>(
                this.missionEditService.missionForm.controls.rpic
            )
        ])
            .pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe$))
            .subscribe(([workgroup, mission, rpic]) => {
                this.selectedWorkgroup = workgroup;
                this.missionDetails = mission;
                this.setupDataService();
            });

        this.missionRpicSelectDataService.personnel$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(personnel => {
                this.updatePersonnelListAndGrouping(personnel);
            });

        this.missionRpicSelectDataService.authorityIds$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ids => (this.authorityIds = ids));
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setupDataService() {
        // In read-only mode the assumption is that the user is not a member of the mission's
        // organisation, so personnel list and register-status fetches would most likely
        // 403.  Build the crewLookup directly from the mission crew details so that person
        // names render correctly even without loading the full personnel list.
        if (this.missionEditService.isReadOnly) {
            this.missionDetails?.missionCrewDetails?.forEach(crew => {
                if (
                    crew.person != null &&
                    this.crewLookup[crew.person.id] == null
                ) {
                    this.crewLookup[crew.person.id] =
                        this.formatPersonPipe.transform(crew.person);
                }
            });
            return;
        }
        this.missionRpicSelectDataService.setParameters(
            this.orgId,
            MissionRpicSelect.REQUIRED_RPIC_ROLES,
            null,
            this.missionDetails,
            this.selectedWorkgroup?.personnelList?.map(p => p.id),
            this.rpicId
        );
    }

    updateCrewLookup() {
        this.pilots.forEach(person => {
            if (this.crewLookup[person.id] == null) {
                this.crewLookup[person.id] =
                    this.formatPersonPipe.transform(person);
            }
        });
    }

    updatePersonnelListAndGrouping(personnel: PersonWithRegisterStatus[]) {
        this.pilots = personnel;
        this.updateCrewLookup();
    }

    writeValue(value: { rpicId: number; rpicRoleId: number } | null): void {
        this.rpicId = value?.rpicId ?? null;
        this.rpicRoleId = value?.rpicRoleId ?? null;
        this.selectPerson = value?.rpicId == null;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    validate(c: FormControl) {
        return this.rpicId != null && this.rpicRoleId != null
            ? this.rpicNotAvailable
                ? {
                      rpicNotAvailable: {
                          message:
                              'This person is no longer available for the role'
                      }
                  }
                : null
            : {
                  length: {
                      valid: this.rpicId != null && this.rpicRoleId != null
                  }
              };
    }

    onUpdate() {
        this.onChange(
            this.rpicId != null
                ? { rpicId: this.rpicId, rpicRoleId: this.rpicRoleId }
                : null
        );
    }

    deleteRow() {
        this.deleteCrew.emit();
    }

    getPilotRegisterStatus(personId: number) {
        return this.pilots?.find(p => p.id === personId)?.registerStatuses;
    }

    toggleShowPilotRegisterDetails() {
        this.showRpicRegisterDetails = !this.showRpicRegisterDetails;
        this.toggleShowDetails.emit();
    }

    onPilotSelectScroll() {
        this.missionRpicSelectDataService.onPersonSelectScroll();
    }

    onPilotSelectSearch(search: any) {
        this.missionRpicSelectDataService.onPersonSelectSearch(search);
    }

    onPilotSelectClose() {
        this.missionRpicSelectDataService.onPersonSelectClose();
    }
}
